<template>
    <div v-if="state === 'connecting'">Connecting</div>
    <v-table v-else-if="state === 'connected'" density="compact">
        <thead>
            <tr>
                <th scope="col" style="width: 32px"></th>
                <th scope="col">Sample Date</th>
                <th scope="col">pH</th>
                <th scope="col">ORP</th>
                <th scope="col">Temp. C</th>
                <th scope="col">RSSI</th>
            </tr>
        </thead>
        <tbody v-if="samples.length === 0">
            <tr>
                <td colspan="7">Waiting for Samples</td>
            </tr>
        </tbody>
        <tbody v-else>
            <template v-for="sample of samples" :key="sample.sampleEpoch">
                <tr
                    class="sample-row"
                    :class="{ 'row-expanded': expandedRow === sample.sampleEpoch }"
                    @click="toggleRow(sample.sampleEpoch)"
                >
                    <td>
                        <v-icon size="16" :icon="expandedRow === sample.sampleEpoch ? 'mdi-chevron-down' : 'mdi-chevron-right'" />
                    </td>
                    <td>{{ formatDate(new Date(sample.sampleDate)) }}</td>
                    <td>{{ sample.ph }}</td>
                    <td>{{ sample.orpMv }}</td>
                    <td>{{ sample.waterTempC }}</td>
                    <td>{{ sample.wifiRssi }}</td>
                </tr>
                <tr v-if="expandedRow === sample.sampleEpoch" class="expanded-detail-row">
                    <td colspan="7">
                        <div class="other-keys-grid">
                            <template v-for="(val, key) in flatOtherKeys(sample.otherKeys)" :key="key">
                                <span class="other-key">{{ key }}</span>
                                <span class="other-val">{{ String(val) }}</span>
                            </template>
                        </div>
                    </td>
                </tr>
            </template>
        </tbody>
    </v-table>
    <div v-else>Failed to connect: {{ error }}</div>
</template>

<script lang="ts" setup>
import { useApi } from '@/api'
import mqtt from 'mqtt'
import { onMounted, onUnmounted, ref } from 'vue'
import type { MonitorLiveStreamV1, MonitorPreviousSampleV1 } from '@geckoal/gecko-api-client'
import { formatDate } from '@/lib/utils'

const props = defineProps({
    monitorId: {
        type: String,
        required: true
    }
})

type LiveSample = {
    sampleEpoch: number
    sampleDate: Date
    ph?: number
    orpMv?: number
    waterTempC?: number
    batteryMv?: number
    wifiRssi?: number
    cpuTempC?: number
    otherKeys?: Record<string, unknown>
}

const samples = ref<LiveSample[]>([])
const state = ref<'connecting' | 'connected' | 'failed'>('connecting')
const error = ref<string | undefined>()
const liveInfo = ref<MonitorLiveStreamV1 | undefined>()
const expandedRow = ref<number | undefined>()

function toggleRow(epoch: number) {
    expandedRow.value = expandedRow.value === epoch ? undefined : epoch
}

// otherKeys may contain a nested object under an "otherKeys" key — flatten it
function flatOtherKeys(otherKeys?: Record<string, unknown>): Record<string, unknown> {
    if (!otherKeys) return {}
    const nested = otherKeys.otherKeys
    if (nested && typeof nested === 'object' && !Array.isArray(nested)) {
        return nested as Record<string, unknown>
    }
    return otherKeys
}

// Holds a pre-fetched fresh URL ready to be used on the next reconnect attempt.
// transformWsUrl must be synchronous, so we fetch the new URL ahead of time and
// swap it in when the MQTT client is about to reconnect.
let nextLiveStreamUrl: string | undefined
// Prevents concurrent in-flight refresh requests
let _refreshInFlight = false
// Set to true when the component is unmounting so we skip any further refreshes
let _isDestroyed = false

let client: mqtt.MqttClient | undefined

async function _fetchLiveStreamInfo() {
    liveInfo.value = await useApi().getMonitorLiveStreamV1({ monitorId: props.monitorId })

    if (liveInfo.value.previousSamples) {
        for (const sample of liveInfo.value.previousSamples) {
            samples.value.push(_toDisplayableSample(sample))
        }
        samples.value = _sort(samples.value)
    }
}

async function _refreshLiveStreamUrl(): Promise<void> {
    if (_isDestroyed || _refreshInFlight) return
    _refreshInFlight = true
    try {
        const freshInfo = await useApi().getMonitorLiveStreamV1({ monitorId: props.monitorId })
        nextLiveStreamUrl = freshInfo.liveStreamUrl
        // Keep liveInfo in sync so topic subscriptions continue to work after reconnect
        liveInfo.value = freshInfo
    } catch (err) {
        console.error('Failed to refresh live stream URL:', err)
    } finally {
        _refreshInFlight = false
    }
}

function _sort(samples: LiveSample[]): LiveSample[] {
    samples.sort((a, b) => {
        return b.sampleEpoch - a.sampleEpoch
    })
    return samples
}

function _toDisplayableSample(sample: MonitorPreviousSampleV1): LiveSample {
    const { sampleEpoch, ph, orpMv, waterTempC, wifiRssi, batteryMv, ...otherKeys } = sample
    return {
        sampleDate: new Date(sample.sampleEpoch * 1000),
        sampleEpoch,
        ph,
        orpMv,
        waterTempC,
        wifiRssi,
        batteryMv,
        otherKeys
    }
}

function _connect() {
    if (client) client.end(true)
    if (!liveInfo.value) throw new Error('must fetch live info first')

    // Reset any pre-fetched URL from a previous session
    nextLiveStreamUrl = undefined
    _refreshInFlight = false

    client = mqtt.connect(liveInfo.value.liveStreamUrl, {
        transformWsUrl: (url: string, _options: unknown, _client: unknown) => {
            // transformWsUrl is called synchronously before each reconnect attempt.
            // We kick off an async refresh now (for the *next* reconnect) and return
            // the URL that was already pre-fetched for *this* reconnect, falling back
            // to the current URL if none is ready yet.
            const urlToUse = nextLiveStreamUrl ?? url
            nextLiveStreamUrl = undefined // consume it

            // Kick off a background refresh so the next reconnect will have a fresh URL ready
            _refreshLiveStreamUrl()

            return urlToUse
        }
    })

    client.on('connect', () => {
        const topic = liveInfo.value?.topic
        if (!topic) throw new Error('must fetch live info topic first')

        client?.subscribe(topic, (err: unknown) => {
            if (err) {
                const message = err instanceof Error ? err.message : String(err)
                console.error('Subscribe error', err)
                error.value = 'Subscribe error: ' + message
                state.value = 'failed'
            } else {
                state.value = 'connected'
            }
        })
    })

    client.on('disconnect', () => {
        state.value = 'connecting'
    })

    client.on('close', () => {
        state.value = 'connecting'
        // Pre-fetch a fresh URL as soon as we detect a close, so it's ready
        // by the time the MQTT client's reconnect timer fires
        _refreshLiveStreamUrl()
    })

    client.on('error', (err: unknown) => {
        const message = err instanceof Error ? err.message : String(err)
        state.value = 'failed'
        error.value = message
    })

    client.on('message', (_topic: string, message: { toString: () => string }) => {
        try {
            const rawSample = JSON.parse(message.toString())

            const samplesArr = samples.value

            samplesArr.unshift(_toDisplayableSample(rawSample as MonitorPreviousSampleV1))
            if (samplesArr.length > 100) samplesArr.pop()

            samples.value = _sort(samples.value)
        } catch (error) {
            console.error(`Error parsing sample: `, message.toString())
        }
    })
}

onUnmounted(() => {
    _isDestroyed = true
    client?.end()
})

onMounted(async () => {
    await _fetchLiveStreamInfo()
    _connect()
})
</script>

<style scoped>
.sample-row {
    cursor: pointer;
    user-select: none;
}
.sample-row:hover td {
    background: rgba(0, 0, 0, 0.03);
}
.row-expanded td {
    background: rgba(0, 0, 0, 0.04);
}
.expanded-detail-row td {
    background: rgba(0, 0, 0, 0.02);
    padding: 10px 16px 12px 48px;
}
.other-keys-grid {
    display: grid;
    grid-template-columns: 220px 1fr;
    gap: 2px 16px;
    font-size: 0.8rem;
}
.other-key {
    font-weight: 500;
    color: rgba(0, 0, 0, 0.6);
}
.other-val {
    color: rgba(0, 0, 0, 0.85);
    font-family: monospace;
}
</style>
